import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6e2qp2tehdqnvnpj70g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/12a76626-876c-406f-84b3-ad0080f444c2.png" alt="OLDTIMER d.o.o." className="h-12 w-12 object-contain" />
              <h1 className="text-2xl font-semibold text-gray-900">OLDTIMER d.o.o.</h1>
            </div>
            <div className="flex items-center gap-12">
              <button onClick={() => scrollToSection('home')} className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('products')} className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-700">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-8 py-32 w-full">
          <div className="max-w-4xl">
            <h2 className="text-7xl font-bold text-white leading-tight mb-8">
              Modern iOS Applications for Enhanced Mobile Experience
            </h2>
            <p className="text-2xl text-white/90 leading-relaxed mb-12">
              We are a team of experts developing cutting-edge applications for the iOS platform, focusing on security, performance, and user satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <h3 className="text-5xl font-bold text-gray-900 mb-16">About OLDTIMER d.o.o.</h3>
          <div className="flex justify-center mb-20">
            <div className="text-center">
              <div className="text-8xl font-bold text-teal-600 mb-4">10+</div>
              <div className="text-xl text-gray-600">Expert Developers</div>
            </div>
          </div>
          <div>
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl">
              OLDTIMER d.o.o. is a dedicated team of iOS development specialists committed to creating innovative mobile solutions. Our expertise spans across security applications, device optimization tools, and custom iOS development. We pride ourselves on delivering high-quality applications that prioritize user privacy, seamless performance, and intuitive design. Our products, including VPN and Cleaner applications, are trusted by thousands of users worldwide who value security and efficiency in their mobile experience.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <h3 className="text-5xl font-bold text-gray-900 mb-20 text-center">Our Products</h3>
          <div className="grid grid-cols-2 gap-12">
            {/* VPN Product */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-12 shadow-xl">
              <div className="mb-8">
                <div className="inline-block px-4 py-2 bg-teal-500 rounded-full text-sm font-semibold text-white mb-6">
                  Security Focused
                </div>
                <h4 className="text-4xl font-bold text-white mb-4">VPN Solution</h4>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Secure and fast VPN service for iOS devices providing comprehensive privacy protection and secure browsing. Our VPN application ensures your online activities remain private with military-grade encryption, no-logs policy, and lightning-fast connection speeds across global servers.
                </p>
              </div>
              <div className="aspect-video bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl flex items-center justify-center">
                <img src="https://public.readdy.ai/ai/img_res/a4ac4341742817995a3ad3fbe717a48c.jpg" alt="VPN Application" className="w-full h-full object-cover rounded-2xl" />
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-12 shadow-xl">
              <div className="mb-8">
                <div className="inline-block px-4 py-2 bg-emerald-600 rounded-full text-sm font-semibold text-white mb-6">
                  Performance Optimizer
                </div>
                <h4 className="text-4xl font-bold text-gray-900 mb-4">Cleaner App</h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Device optimization tool that helps users clean junk files, manage storage, and improve device performance. Our Cleaner application intelligently identifies unnecessary files, optimizes memory usage, and provides detailed storage analytics to keep your iOS device running smoothly and efficiently.
                </p>
              </div>
              <div className="aspect-video bg-gradient-to-br from-white to-gray-100 rounded-2xl flex items-center justify-center">
                <img src="https://readdy.ai/api/search-image?query=modern%20minimalist%20iOS%20cleaner%20application%20interface%20displaying%20storage%20analysis%20with%20circular%20progress%20charts%20and%20clean%20file%20management%20on%20white%20background%20with%20emerald%20green%20accent%20colors%20professional%20mobile%20app%20design&width=600&height=400&seq=cleaner001&orientation=landscape" alt="Cleaner Application" className="w-full h-full object-cover rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <h3 className="text-5xl font-bold text-gray-900 mb-4 text-center">
            Platform <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">Features</span>
          </h3>
          <p className="text-xl text-gray-600 text-center mb-20 max-w-3xl mx-auto">
            Our iOS applications are built with cutting-edge technology and user-centric design principles to deliver exceptional mobile experiences.
          </p>
          <div className="grid grid-cols-2 gap-12">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4 text-center">Advanced Security</h4>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Military-grade encryption and robust security protocols protect your data and privacy. Our applications implement the latest security standards to ensure your information remains confidential and secure at all times.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-speed-line text-4xl text-white"></i>
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4 text-center">High Performance</h4>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Optimized code and efficient algorithms ensure smooth operation and minimal battery consumption. Our apps are designed to deliver maximum performance while maintaining excellent resource management.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-user-heart-line text-4xl text-white"></i>
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4 text-center">User-Friendly Design</h4>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Intuitive interfaces and seamless user experiences make our applications accessible to everyone. We focus on creating designs that are both beautiful and functional, ensuring users can navigate effortlessly.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-customer-service-2-line text-4xl text-white"></i>
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4 text-center">24/7 Support</h4>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Dedicated customer support team available around the clock to assist with any questions or issues. We are committed to providing exceptional service and ensuring your satisfaction with our products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-20">
            {/* Contact Information */}
            <div>
              <h3 className="text-5xl font-bold text-gray-900 mb-12">Get in Touch</h3>
              <p className="text-xl text-gray-700 mb-16 leading-relaxed">
                Have questions about our products or services? We'd love to hear from you. Reach out to our team and we'll respond as soon as possible.
              </p>
              
              <div className="space-y-10">
                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-map-pin-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Address</div>
                      <div className="text-base text-gray-700">Modric bb</div>
                      <div className="text-base text-gray-700">City Domanovici</div>
                      <div className="text-base text-gray-700">Zip 88305</div>
                      <div className="text-base text-gray-700">Country Bosnia and Herzegovina</div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-mail-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Email</div>
                      <a href="mailto:contact@oldtimerdoo.com" className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">contact@oldtimerdoo.com</a>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center mt-1">
                      <i className="ri-phone-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Number</div>
                      <a href="tel:+17744972542" className="text-base text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">+1 7744972542</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-12 shadow-xl">
              <h4 className="text-3xl font-bold text-gray-900 mb-8">Send us a Message</h4>
              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-base"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-base"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-base"
                    placeholder="How can we help?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-base"
                    placeholder="Your message (max 500 characters)"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-semibold py-4 rounded-lg hover:from-teal-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap text-base"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/12a76626-876c-406f-84b3-ad0080f444c2.png" alt="OLDTIMER d.o.o." className="h-10 w-10 object-contain" />
              <div>
                <div className="text-2xl font-bold">OLDTIMER d.o.o.</div>
                <div className="text-sm text-gray-400">iOS Development Experts</div>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              © 2025 OLDTIMER d.o.o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
